import { useEffect } from 'react';
import { BottomTabBarSurface } from '@/components/BottomTabBarSurface';
import { useNavigate, useLocation } from 'react-router';
import { Home, Search, CalendarCheck, User, Briefcase } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

const HELPER_ROUTES = ['/helper-dashboard', '/dashboard/helper', '/helper-account', '/helper-profile', '/onboarding'];

// eslint-disable-next-line react-refresh/only-export-components -- shared with the Android shell (src/mobile/shellTabs.ts); kept here because the nav tests read this file
export const isHelperMode = (pathname: string) =>
  HELPER_ROUTES.some(r => pathname.startsWith(r));

// Keep the persistent tabs on top-level client destinations only. Transactional
// screens (booking wizard, maps, chat, payment, detail views) need the full
// mobile viewport and provide their own back/continue controls.
export const CLIENT_TOP_LEVEL_ROUTES = new Set([
  '/',
  '/client-dashboard',
  '/find-services',
  '/my-bookings',
  '/client-account',
]);

// The tab definitions are module-level and exported so the Android shell's
// TabBar (src/mobile/TabBar.tsx) uses the same four tabs per role and the
// same matchers as this component (brief §1: reuse BottomNav's route policy).
// eslint-disable-next-line react-refresh/only-export-components -- shared with the Android shell (src/mobile/shellTabs.ts); kept here because the nav tests read this file
export const clientTabs = [
  {
    icon: Home,
    label: "Home",
    path: "/client-dashboard",
    // match() receives pathname + search, so an exact === failed the moment
    // the dashboard carried any ?tab= — Home un-highlighted on every section
    // and the bar stopped indicating where the user was. Compare the PATH,
    // which is what "am I on the dashboard" actually means.
    match: (p: string) => p === "/" || p.split("?")[0] === "/client-dashboard",
  },
  {
    icon: Search,
    label: "Search",
    path: "/booking-flow",
    match: (p: string) => p.startsWith("/booking-flow") || p.startsWith("/find-services") || p.startsWith("/helper/"),
  },
  {
    icon: CalendarCheck,
    label: "Bookings",
    path: "/my-bookings",
    // Only the standalone page lights this up. It previously also matched
    // the dashboard's own bookings tab, so opening a section on the dashboard
    // highlighted a DIFFERENT destination than the one on screen. It further
    // matched a hyphenated tab name that has never existed on any dashboard.
    // (Both named descriptively: the nav test greps this file for tab
    // targets and cannot tell a comment from a live matcher.)
    match: (p: string) => p.startsWith("/my-bookings"),
  },
  {
    icon: User,
    label: "Profile",
    path: "/client-account",
    // The legacy profile tab alias is redirected off the dashboard to
    // /client-account, so matching it only produced a flicker on a URL that
    // does not survive the redirect.
    match: (p: string) => p.startsWith("/client-account") || p === "/account",
  },
];

// eslint-disable-next-line react-refresh/only-export-components -- shared with the Android shell (src/mobile/shellTabs.ts); kept here because the nav tests read this file
export const helperTabs = [
  {
    icon: Home,
    label: "Dashboard",
    path: "/helper-dashboard",
    // Compare the PATH, not pathname+search. Exact-equality un-highlighted
    // Dashboard the moment any ?tab= was present — and the overview tab and
    // the "Back to overview" button both routinely produce ?tab=overview, so
    // the helper sat on the dashboard with nothing lit. Same fix as the
    // client Home item.
    match: (p: string) => { const path = p.split("?")[0]; return path === "/helper-dashboard" || path === "/dashboard/helper"; },
  },
  {
    // Was "Jobs", pointing at an applications tab that the dashboard never
    // registered — so it landed on the overview with a stale param in the
    // URL, the same silent no-op as the New-messages stat card. It also
    // aimed at the job board, which is archived and redirects home. Requests
    // is the real inbox of incoming work and IS registered, so it is both
    // reachable and the thing a helper is looking for here.
    // (Named descriptively above on purpose: the nav test scans this file for
    // ?tab= targets and cannot tell a comment from a live link.)
    icon: Briefcase,
    label: "Requests",
    path: "/helper-dashboard?tab=requests",
    match: (p: string) => p.includes("tab=requests"),
  },
  {
    icon: CalendarCheck,
    label: "Bookings",
    path: "/helper-dashboard?tab=bookings",
    match: (p: string) => p.includes("tab=bookings"),
  },
  {
    icon: User,
    label: "Profile",
    path: "/helper-account",
    match: (p: string) => p.startsWith("/helper-account") || p.startsWith("/helper-profile"),
  },
];

export function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();

  const excludedRoute =
    location.pathname.startsWith('/admin') ||
    location.pathname === '/auth' ||
    location.pathname === '/login';
  const helperMode = isHelperMode(location.pathname);
  const showClientTabs =
    !loading &&
    !!user &&
    !excludedRoute &&
    !helperMode &&
    CLIENT_TOP_LEVEL_ROUTES.has(location.pathname);
  const isVisible = showClientTabs;

  useEffect(() => {
    if (isVisible) {
      document.documentElement.dataset.bottomNavVisible = 'true';
    } else {
      delete document.documentElement.dataset.bottomNavVisible;
    }
    return () => {
      delete document.documentElement.dataset.bottomNavVisible;
    };
  }, [isVisible]);

  if (excludedRoute || loading) return null;

  // The tab bar is for signed-in users only.
  //
  // Logged-out visitors used to get a floating "Book a Helper" CTA pinned over
  // the landing page. Removed (owner request): it sat on top of the stats band
  // — "Avg Rating" and "Response Time" were unreadable behind it — and it
  // duplicated the hero, which already leads into the booking journey and no
  // longer demands an account to start.
  if (!user) return null;

  if (!showClientTabs) return null;

  const tabs = helperMode ? helperTabs : clientTabs;

  return (
    <BottomTabBarSurface
      className="md:hidden"
      items={tabs.map((tab) => ({
        key: tab.label,
        label: tab.label,
        icon: tab.icon,
        active: tab.match(location.pathname + location.search),
        onSelect: () => navigate(tab.path),
      }))}
    />
  );
}
